// ============================================================================
// Permission predicates - the ONE place page + programmed-list access is decided
// ============================================================================
// Pure functions derived from a profile's permission flags. Pass the profile
// explicitly (almost always `auth.profile`, which is populated at app boot).
// Kept dependency-free (no app/ imports) so both feature pages and the
// dependency-light default-sidebar-layout config can share the same rules.
//
// The JS twin of the database's permission helpers. A rule tagged
// `@sql private.<name>()` mirrors that helper, whose rule is the pure SQL
// function private.perm_<name>(profile) applied to the caller's own row. For
// every profile row the JS must answer exactly what perm_<name> answers:
// permissions.test.mjs proves it over a synthetic matrix of every role and
// flag combination, against permissions.golden.mjs, which Postgres produced.
// Change a tagged rule and its SQL twin together, then regenerate the golden.
//
// INACTIVE GRANTS NOTHING. Every predicate that answers "may THIS user do X"
// starts with isStaff(), so a missing profile (a sign-in with no profiles row)
// and a leaver (role 'inactive') get false, whatever flags the leaver's row
// still carries. A few arguments name a SUBJECT - a person being listed or
// offered - rather than the acting user. The rule judges the actor only, and
// each JSDoc says which argument is which.
//
// Roles in use: 'admin', 'employee', 'inactive'. The enum also carries 'owner'
// and 'staff', which no profile holds and no rule names - they answer exactly
// like 'employee'.
//
// Flag -> role mapping (see Settings > Users):
//   can_manage_leave     = HR (leave management)
//   can_approve_leave    = leave approver (first approval step)
//   can_view_leave       = leave viewer (read-only)
//   can_approve_billing  = Billing
//   is_partner           = Partner
//   is_timesheet_manager = timesheet manager (Time Review)
//   can_sign_off_budgets = budget signatory (also edits report config)
// ============================================================================

/**
 * The slice of a `profiles` row these rules read. `auth.profile` carries all
 * of it. A partial row works too: an absent flag reads as false and an absent
 * or NULL role as active - only an explicit 'inactive' is refused.
 * @typedef {object} Profile
 * @property {string} [user_id]
 * @property {'admin'|'employee'|'inactive'|'owner'|'staff'|null} [role]
 * @property {boolean|null} [can_manage_leave]
 * @property {boolean|null} [can_approve_leave]
 * @property {boolean|null} [can_view_leave]
 * @property {boolean|null} [can_approve_billing]
 * @property {boolean|null} [is_partner]
 * @property {boolean|null} [is_timesheet_manager]
 * @property {boolean|null} [receives_billing_emails]
 * @property {boolean|null} [can_sign_off_budgets]
 */

/**
 * A Profile that isStaff() accepted: any role but 'inactive'.
 * @typedef {Profile & { role?: Exclude<Profile['role'], 'inactive'> }} ActiveProfile
 */

// ----------------------------------------------------------------------------
// Who the user IS - the building blocks every page gate below is made of
// ----------------------------------------------------------------------------

/**
 * Whether the profile is active staff: a profiles row whose role is not
 * 'inactive'. The gate every actor rule in this file starts with.
 *
 * Only a true answer narrows the type. A false one proves nothing about it: a
 * leaver's row is still a Profile, and that is what tells a leaver from a
 * sign-in with no profile row at all.
 * @sql private.is_staff()
 * @param {Profile|null|undefined} profile - the acting user (usually `auth.profile`)
 * @returns {profile is ActiveProfile}
 */
export function isStaff(profile) {
    return !!profile && profile.role !== 'inactive';
}

/**
 * Whether the profile is an admin. 'admin' is never 'inactive', so isStaff
 * changes nothing here - it is there so every rule reads the same way.
 * @sql private.is_admin()
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function isAdmin(profile) {
    return isStaff(profile) && profile.role === 'admin';
}

/**
 * Whether the profile is HR: sees and manages every leave and overtime
 * request, takes the HR approval step and pays overtime.
 * @sql private.is_hr()
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canManageLeave(profile) {
    return isStaff(profile) && !!profile.can_manage_leave;
}

/**
 * Whether the profile may approve leave as a manager - the people offered in
 * the leave approver picker. The first approval step only; HR is
 * canManageLeave.
 * @sql private.is_leave_approver()
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function isLeaveApprover(profile) {
    return isStaff(profile) && !!profile.can_approve_leave;
}

/**
 * Whether the profile approves leave or overtime at all - a leave approver or
 * HR. Who gets the Leave Mgmt tab and the overtime approvals, and who can be
 * picked to approve overtime.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function isLeaveApproverOrHr(profile) {
    return isLeaveApprover(profile) || canManageLeave(profile);
}

/**
 * Whether the profile is a partner. An AUTHORITY flag, not just the email
 * subscription Settings presents it as: it opens the finance pages and Time
 * Review, and lets the holder add options to shared fields.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function isPartner(profile) {
    return isStaff(profile) && !!profile.is_partner;
}

/**
 * Whether the profile may APPROVE billing: approve or reject a report under
 * review, edit it while it waits, defer its checklist, delink an estimate
 * settlement. The flag alone - an admin without it may not approve, exactly
 * as fn_billing_approve, fn_billing_defer_checklist,
 * fn_billing_clear_checklist_deferral and fn_billing_delink_settlement refuse
 * one.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canApproveBilling(profile) {
    return isStaff(profile) && !!profile.can_approve_billing;
}

/**
 * The "billing manager" rule: admins and billing approvers - the people who
 * may write money onto bills and correct them after approval (record an
 * invoice, mark paid, revert paid, delay, edit invoice details, retainer
 * exclusion, reverse a write-off). Distinct from and TIGHTER than
 * financeAccess: HR and partners get the finance PAGES but never this. WIDER
 * than canApproveBilling: an admin without the flag manages but does not
 * approve.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canManageBilling(profile) {
    return isStaff(profile) && (profile.role === 'admin' || !!profile.can_approve_billing);
}

/**
 * Whether the profile may edit report configuration. Admins and budget
 * signatories: the rule the database applies to the report settings it guards
 * (the CIPC, compliance and non-billable-time report keys in system_config,
 * and fn_compliance_cron_set_active). No screen gates on it yet.
 * @sql private.can_manage_report_config()
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canManageReportConfig(profile) {
    return isStaff(profile) && (profile.role === 'admin' || !!profile.can_sign_off_budgets);
}

// ----------------------------------------------------------------------------
// Page and feature gates
// ----------------------------------------------------------------------------

/**
 * The "management finance" gate shared by Billing, Retainers and Supplier
 * Bills: admins, HR (leave managers), billing approvers, and partners.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
function financeAccess(profile) {
    return isStaff(profile) && (profile.role === 'admin'
        || !!(profile.can_manage_leave || profile.can_approve_billing || profile.is_partner));
}

/**
 * Whether the profile may access the Billing page.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAccessBilling(profile) {
    return financeAccess(profile);
}

/**
 * Who a bulk billing batch is submitted TO for review - the default recipient
 * set of the bulk-prepare notification.
 *
 * Deliberately NOT every `receives_billing_emails` approver: that flag is the
 * INVOICING digest subscription ("Bill Email" in Settings, the daily
 * billing-approved list), and the invoicing admin carries it to pick work up
 * AFTER approval, not while it waits for review. Requiring `is_partner` too
 * narrows it to the reviewing partner(s).
 *
 * There is deliberately NO fallback when nobody matches. An empty default is a
 * visible, blocking state in the pre-flight modal (the confirm button stays
 * disabled until someone is picked); widening back to every flagged approver
 * would silently restore the invoicing admin, which is the outcome this rule
 * exists to prevent.
 *
 * A SUBJECT rule, not an actor rule: `profile` is a candidate reviewer, not
 * the person preparing the batch, so the inactive rule is not applied here -
 * which people are offered at all is the recipient query's call.
 *
 * NOTE: both flags are presented in Settings as email subscriptions with no
 * mention of review, so this rule is only as stable as those two toggles. If
 * it needs to survive someone re-ticking them for unrelated reasons, it wants
 * a `receives_billing_review_emails` column of its own.
 * @param {Profile|null|undefined} profile - the SUBJECT: one candidate reviewer
 * @returns {boolean}
 */
export function isBillingReviewer(profile) {
    if (!profile) return false;
    return !!(profile.can_approve_billing && profile.receives_billing_emails && profile.is_partner);
}

/**
 * Whether the profile may access the Retainers page.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAccessRetainers(profile) {
    return financeAccess(profile);
}

/**
 * Whether the profile may access the Supplier Bills page.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAccessSupplierBills(profile) {
    return financeAccess(profile);
}

/**
 * Whether the profile may manage a client's discounts / complexity fees (the
 * suggestion config behind the client Services tab). Tighter than
 * financeAccess(): this writes the rules that land money on future bills, so it
 * is canManageBilling - HR and partners get the finance PAGES but not this.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canManageFeeModifiers(profile) {
    return canManageBilling(profile);
}

/**
 * Whether the profile may manage a client's retainer ledger.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canManageRetainers(profile) {
    return canManageBilling(profile);
}

/**
 * Whether the profile may merge one client into another.
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canMergeClients(profile) {
    return canManageBilling(profile);
}

/**
 * Whether the profile may access the Leave Calendar (view or manage leave).
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAccessLeaveCalendar(profile) {
    return isStaff(profile) && !!(profile.can_view_leave || profile.can_manage_leave);
}

/**
 * Whether the profile may see an employee's leave ENTITLEMENT figures
 * (allocated / taken / left). This is the exact client mirror of the SELECT
 * policies on `leave_allocations`: your own, HR, any leave approver, or the
 * approver assigned to the request being viewed. Nothing else - notably NOT
 * `role === 'admin'` and NOT `can_view_leave`, neither of which appears in any
 * policy, so a viewer holding only those reads an RLS-filtered EMPTY set that
 * looks identical to "HR never set an allocation". Rendering a balance panel
 * without this gate paints fabricated zeros over real entitlements.
 *
 * Deliberately narrower than the DB on the approver arm: the policy grants read
 * when you are approver on ANY of that employee's leave, while this only knows
 * about the request in hand. Narrower is safe - it can hide, never over-show.
 * The same goes for a leaver: the isStaff gate refuses even their own balance.
 *
 * Day-level leave (who is off, when) is broad by design; entitlement NUMBERS
 * encode seniority and negotiated terms, so they stay on this tighter gate.
 *
 * @param {string|null|undefined} employeeId - the SUBJECT: whose balance is being shown
 * @param {Profile|null|undefined} profile - the acting user (usually `auth.profile`)
 * @param {string|null} [approverId] - approver_id of the request being viewed
 * @returns {boolean}
 */
export function canViewLeaveBalance(employeeId, profile, approverId = null) {
    if (!isStaff(profile) || !employeeId) return false;
    return profile.user_id === employeeId
        || !!profile.can_manage_leave
        || !!profile.can_approve_leave
        || (!!approverId && profile.user_id === approverId);
}

/**
 * Whether the profile may open the Time Review report (any staff member's
 * captured time, day by day, plus the stats over it). Management only: admins,
 * partners, HR (leave managers), and the designated timesheet managers - the
 * people who already receive the weekly timesheet review digest
 * (`profiles.is_timesheet_manager`).
 *
 * Like every other predicate here this gates a SCREEN, not data: RLS on
 * `timesheet_entries` lets any staff member read every row, so this decides who
 * the report is FOR, it does not make anything confidential.
 *
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canReviewTimesheets(profile) {
    return isStaff(profile) && (profile.role === 'admin'
        || !!(profile.is_partner || profile.can_manage_leave || profile.is_timesheet_manager));
}

/**
 * Whether the profile may append new options to a shared custom-field
 * definition via the inline "+ Add option" affordances (checklist select /
 * multi_select, grid dropdown footers). This mutates the field definition for
 * EVERY task of the service, so it is limited to admins and partners -
 * everyone else picks from the admin-curated list (or uses the per-task
 * "Other" answer where the field opts in via options.allowOther).
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAppendFieldOptions(profile) {
    return isStaff(profile) && (profile.role === 'admin' || !!profile.is_partner);
}

// Time Review is a management report ABOUT people, and one person is
// deliberately not part of that conversation: the developer, whose captured
// time is work on this app rather than firm work anyone reviews. He still needs
// his own report, so the rule is "self only", not "gone".
//
// A user id list rather than a role check, because no role or flag expresses
// "leave me out of the report". It hides a NAME, not data: every timesheet row
// behind it stays readable to any staff member, so there is nothing here to
// leak.
const TIME_REVIEW_PRIVATE_USER_IDS = new Set([
    '6436124e-9ed4-4fc8-8116-3d50588f6d58' // Joshua Nevin
]);

/**
 * Whether `personId` may be shown as a SUBJECT of the Time Review report.
 *
 * Firm-wide lists pass NO viewer - a list reads the same for everyone who opens
 * it - so a private person never appears in one. The person picker passes the
 * viewer, which is what lets someone hidden from the firm still open their own
 * report.
 *
 * `personId` is the SUBJECT and is not judged by the inactive rule (whether a
 * leaver is listed is the caller's call); `viewer` is the actor, so an
 * inactive viewer never unlocks a private report, not even their own. The
 * isStaff gate sits on the viewer arm only - at the top it would empty every
 * firm-wide list, which passes no viewer at all.
 *
 * @param {string|null|undefined} personId - the SUBJECT: the person being listed / picked
 * @param {Profile|null} [viewer] - the acting user (usually `auth.profile`)
 * @returns {boolean}
 */
export function isTimeReviewSubject(personId, viewer = null) {
    if (!personId) return false;
    if (!TIME_REVIEW_PRIVATE_USER_IDS.has(personId)) return true;
    return isStaff(viewer) && viewer.user_id === personId;
}

// Programmed-list id -> access predicate. Any programmed list NOT listed here
// (Tasks, Reports, Activity, Stats, ...) is open to all staff. This is what
// hides the "Management" lists from the sidebar, the Lists page, and the
// default sidebar template for users without the matching role.
/** @type {Record<string, (profile: Profile|null|undefined) => boolean>} */
const PROGRAMMED_LIST_ACCESS = {
    'billing-programmed': canAccessBilling,
    'retainers-programmed': canAccessRetainers,
    'supplier-bills-programmed': canAccessSupplierBills,
    'leave-calendar-programmed': canAccessLeaveCalendar
};

/**
 * Whether a programmed list should be visible / pinnable for a profile.
 * Non-restricted lists (and every custom list id) are open to all staff; a
 * missing or inactive profile sees none.
 * @param {string} listId
 * @param {Profile|null|undefined} profile - the acting user
 * @returns {boolean}
 */
export function canAccessProgrammedList(listId, profile) {
    if (!isStaff(profile)) return false;
    const check = PROGRAMMED_LIST_ACCESS[listId];
    return check ? check(profile) : true;
}
